import { useState, useEffect } from 'react';
import { motion } from 'framer-motion';
import { Card } from '../ui/card';
import { Skeleton } from '../ui/skeleton';
import { NumberTicker } from '../ui/number-ticker';
import { Tooltip, TooltipContent, TooltipTrigger } from '../ui/tooltip';
import { Info } from 'lucide-react';
import AnalyticsApiService, { LiveMetrics } from '../../services/analyticsApi';

interface LiveStatsPanelProps {
  className?: string;
}

export function LiveStatsPanel({ className }: LiveStatsPanelProps) {
  const [metrics, setMetrics] = useState<LiveMetrics | null>(null);
  const [isLoading, setIsLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [lastUpdated, setLastUpdated] = useState<Date>(new Date());

  const fetchLiveMetrics = async () => {
    try {
      const data = await AnalyticsApiService.getLiveMetrics();
      setMetrics(data);
      setLastUpdated(new Date());
      setError(null);
    } catch (error) {
      console.error('Failed to fetch live metrics:', error);
      setError('Failed to load live metrics');
    } finally {
      setIsLoading(false);
    }
  };

  // Initial load
  useEffect(() => {
    fetchLiveMetrics();
  }, []);

  // Auto-refresh every minute (60000ms)
  useEffect(() => {
    const interval = setInterval(() => {
      console.log('Auto-refreshing live metrics...');
      fetchLiveMetrics();
    }, 60000); // 1 minute

    return () => clearInterval(interval);
  }, []);

  if (error) {
    return (
      <Card className={`p-6 bg-red-50 dark:bg-red-950/30 border border-red-200 dark:border-red-800 ${className}`}>
        <p className="text-red-600 dark:text-red-400 text-sm">{error}</p>
      </Card>
    );
  }

  return (
    <div className={className}>
      {/* Header */}
      <div className="mb-6">
        <div className="flex items-center justify-between">
          <div>
            <h2 className="text-3xl font-['SF_Pro_Display',_-apple-system,_sans-serif] font-bold text-gray-900 dark:text-gray-100 tracking-[-0.02em]">
              Live Stats
            </h2>
            <div className="flex items-center gap-2 mt-2">
              <div className="flex items-center gap-1.5">
                <div className="w-2 h-2 bg-green-500 rounded-full animate-pulse" />
                <p className="text-sm font-['SF_Pro_Text',_-apple-system,_sans-serif] text-gray-600 dark:text-gray-400">
                  Updates every minute
                </p>
              </div>
              <span className="text-gray-300 dark:text-gray-600">•</span>
              <p className="text-xs font-['SF_Pro_Text',_-apple-system,_sans-serif] text-gray-400 dark:text-gray-500">
                Last updated: {lastUpdated.toLocaleTimeString(undefined, { 
                  hour: 'numeric', 
                  minute: '2-digit',
                  second: '2-digit',
                  hour12: true 
                })}
              </p>
            </div>
          </div>

          {/* Manual Refresh */}
          <motion.button
            onClick={fetchLiveMetrics}
            disabled={isLoading}
            whileHover={!isLoading ? { scale: 1.05 } : {}}
            whileTap={!isLoading ? { scale: 0.95 } : {}}
            className={`p-3 rounded-xl transition-all duration-200 ${
              isLoading
                ? 'bg-gray-100 dark:bg-gray-800 text-gray-400 dark:text-gray-600 cursor-not-allowed'
                : 'bg-blue-500 text-white hover:bg-blue-600 shadow-lg hover:shadow-xl'
            }`}
          >
            <svg 
              className={`w-5 h-5 ${isLoading ? 'animate-spin' : ''}`}
              fill="none" 
              stroke="currentColor" 
              viewBox="0 0 24 24"
            >
              <path 
                strokeLinecap="round" 
                strokeLinejoin="round" 
                strokeWidth={2} 
                d="M4 4v5h.582m15.356 2A8.001 8.001 0 004.582 9m0 0H9m11 11v-5h-.581m0 0a8.003 8.003 0 01-15.357-2m15.357 2H15" 
              />
            </svg>
          </motion.button>
        </div>
      </div>

      {/* Stats Grid */}
      <div className="grid grid-cols-1 gap-6 justify-items-center">
        {/* Total Users */}
        <motion.div
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ delay: 0.1 }}
          className="w-full max-w-xl"
        >
          <Card className="bg-gradient-to-br from-indigo-50 to-indigo-100/50 dark:from-indigo-950/30 dark:to-indigo-900/30 border border-indigo-200 dark:border-indigo-800 p-7 hover:shadow-xl transition-shadow min-h-[180px] flex flex-col">
            <div className="flex items-start justify-between mb-3">
              <div className="w-12 h-12 bg-indigo-500 rounded-2xl flex items-center justify-center shadow-lg">
                <svg className="w-6 h-6 text-white" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M17 20h5v-2a3 3 0 00-5.356-1.857M17 20H7m10 0v-2c0-.656-.126-1.283-.356-1.857M7 20H2v-2a3 3 0 015.356-1.857M7 20v-2c0-.656.126-1.283.356-1.857m0 0a5.002 5.002 0 019.288 0M15 7a3 3 0 11-6 0 3 3 0 016 0zm6 3a2 2 0 11-4 0 2 2 0 014 0zM7 10a2 2 0 11-4 0 2 2 0 014 0z" />
                </svg>
              </div>
              <span className="text-xs font-['SF_Pro_Text',_-apple-system,_sans-serif] font-medium text-indigo-600 dark:text-indigo-400 bg-indigo-100 dark:bg-indigo-900/50 px-2 py-1 rounded-lg">
                All Time
              </span>
            </div>
            <div className="flex items-center gap-2 mb-2">
              <h3 className="text-sm font-['SF_Pro_Text',_-apple-system,_sans-serif] font-medium text-indigo-900 dark:text-indigo-200">
                Total Users
              </h3>
              <Tooltip>
                <TooltipTrigger asChild>
                  <button className="text-indigo-500 dark:text-indigo-400 hover:text-indigo-600 dark:hover:text-indigo-300 transition-colors">
                    <Info className="h-3.5 w-3.5" />
                  </button>
                </TooltipTrigger>
                <TooltipContent className="bg-indigo-900/95 dark:bg-indigo-950/95 backdrop-blur-sm border-indigo-700 dark:border-indigo-600 text-white">
                  <p className="text-xs">All registered users (user_type='user')</p>
                </TooltipContent>
              </Tooltip>
            </div>
            {isLoading ? (
              <Skeleton className="h-10 w-24 bg-indigo-200/60 dark:bg-indigo-800/60" />
            ) : (
              <NumberTicker
                value={metrics?.total_users || 0}
                className="text-4xl font-['SF_Pro_Display',_-apple-system,_sans-serif] font-bold text-indigo-600 dark:text-indigo-400"
                decimalPlaces={0}
                direction="up"
              />
            )}
          </Card>
        </motion.div>
      </div>
    </div>
  );
}
